import {DatabaseInfo} from "@app/core-app/app/data/notion/DatabaseInfo";
import {InboxFieldNames} from "@app/core-app/app/domain/notion/inbox";

export type PropertyConfiguration<T> = {
    property: string | null | undefined,
    optional?: boolean | null | undefined
    selectedProperty: string | null | undefined
    modify?: (fields: T | undefined | null, newVal: string) => T
    name: string,
    description: string
    ok?: boolean | undefined
}

// Usage with InboxFieldNames
export type InboxPropertyConfiguration = PropertyConfiguration<InboxFieldNames>;

export const getInboxProperties = (database: DatabaseInfo): InboxPropertyConfiguration[] => {
  const customFields = database.inboxFieldNames; // Ensure this structure exists in your DatabaseInfo type

  return [
    {
      property: database.inboxReport.nameProperty,
      selectedProperty: customFields.name,
      modify: (fields, newVal) => ({...(fields || customFields), name: newVal}),
      name: "Subject property:",
      description: "Specify the property to use for the email's subject. This should be a 'title' property."
    },
    {
      property: database.inboxReport.fromProperty,
      selectedProperty: customFields.from,
      modify: (fields, newVal) => ({...(fields || customFields), from: newVal}),
      name: "Email sender property:",
      description: "Designate the property to store the sender's email address. This should be an 'email' type property."
    },
    {
      property: database.inboxReport.toProperty,
      selectedProperty: customFields.to,
      modify: (fields, newVal) => ({...(fields || customFields), to: newVal}),
      name: "Email recipients property:",
      description: "Identify the property for storing recipient email addresses. This should also be an 'email' type property."
    },
    {
      property: database.inboxReport.dateReceivedProperty,
      selectedProperty: customFields.dateReceived,
      modify: (fields, newVal) => ({...(fields || customFields), dateReceived: newVal}),
      name: "Date Received property:",
      description: "Select the property that will record the date an email was received. This should be a 'date' type property."
    },
    {
      property: database.inboxReport.attachmentsProperty,
      selectedProperty: customFields.attachments,
      modify: (fields, newVal) => ({...(fields || customFields), attachments: newVal}),
      name: "Attachments property:",
      description: "Choose the property to store attachments. This property should be of the 'files' type to hold file links or data."
    },
    {
      property: database.inboxReport.previewImageProperty,
      selectedProperty: customFields.previewImage,
      modify: (fields, newVal) => ({...(fields || customFields), previewImage: newVal}),
      name: "Email Preview property:",
      description: "Select the property to hold a preview image of the email, if applicable. This should be a 'files' type property."
    },
    {
      property: database.inboxReport.emailImageProperty,
      selectedProperty: customFields.emailImage,
      modify: (fields, newVal) => ({...(fields || customFields), emailImage: newVal}),
      name: "Email Image property:",
      description: "Define the property for storing the email as an image file, if needed. This will be a 'files' type property."
    },
    {
      property: database.inboxReport.emailTextProperty,
      selectedProperty: customFields.emailText,
      modify: (fields, newVal) => ({...(fields || customFields), emailText: newVal}),
      optional: true,
      name: "Email Text property:",
      description: "Specify the property to save the textual content of the email. This should be a 'rich text' or 'text' type property."
    },
    {
      property: database.inboxReport.replyToProperty,
      selectedProperty: customFields.replyTo,
      modify: (fields, newVal) => ({...(fields || customFields), replyTo: newVal}),
      optional: true,
      name: "Reply To property:",
      description: "Choose the property that links to the reply-to email within the same or another database. This should be a 'relation' type property."
    }
  ];
};
